import { IconButton, Tooltip } from '@ds';
import { PlusRoundFilled } from '@icons';
import { LegConnectorConnectionType } from '@lp/features/RoutePlanner/types';
import { SegmentTypeEnum, RouteLegTypeEnum } from '@/api/apis/route';

type LegConnectorProps = {
  type: RouteLegTypeEnum;
  startConnection: LegConnectorConnectionType;
  endConnection: LegConnectorConnectionType;
  segmentType: string;
  isDisableAddLegButton?: boolean;
  disableHintText?: string;
  showAddLegButton?: boolean;
  showStartConnector?: boolean;
  showBottomConnector?: boolean;
  onAddLegClick?: () => void;
};

const getMainConnectorColor = (segmentType: string) => {
  switch (segmentType) {
    case SegmentTypeEnum.hospital:
      return '#E78521';
    case SegmentTypeEnum.txcenter:
      return '#9B1C1C';
    case SegmentTypeEnum.hub:
    case SegmentTypeEnum.dhToHub:
      return '#0F8B99';
    case SegmentTypeEnum.reposition:
    case SegmentTypeEnum.returnToBase:
    case SegmentTypeEnum.custom:
      return '#0F8B99';
    default:
      return '#E78521';
  }
};

const getInbetweenConnectorColor = (segmentType: string) => {
  switch (segmentType) {
    case SegmentTypeEnum.hospital:
      return '#F6D1AC';
    case SegmentTypeEnum.txcenter:
      return '#EAA9A9';
    case SegmentTypeEnum.hub:
    case SegmentTypeEnum.dhToHub:
      return '#83CFD8';
    case SegmentTypeEnum.reposition:
    case SegmentTypeEnum.returnToBase:
    case SegmentTypeEnum.custom:
      return '#C7C9D3';
    default:
      return '#F6D1AC';
  }
};

const getConnectorColor = ({
  connection,
  segmentType,
}: {
  connection: LegConnectorConnectionType;
  segmentType: string;
}) => {
  switch (connection) {
    case LegConnectorConnectionType.inbetween:
      return getInbetweenConnectorColor(segmentType);
    case LegConnectorConnectionType.startToEnd:
      return getMainConnectorColor(segmentType);
    default:
      return '#C7C9D3';
  }
};

const getBorderStyle = ({ type, segmentType }: Pick<LegConnectorProps, 'type' | 'segmentType'>) => {
  switch (type) {
    case RouteLegTypeEnum.TmdxJetAviaLeg:
    case RouteLegTypeEnum.SupplementalLiftAviaLeg:
    case RouteLegTypeEnum.CommercialFlightAviaLeg:
    case RouteLegTypeEnum.TxCenterProviderAviaLeg:
      return segmentType === SegmentTypeEnum.reposition ? 'dashed' : 'dotted';
    case RouteLegTypeEnum.TmdxAffiliateGroundLeg:
    case RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg:
    case RouteLegTypeEnum.TmdxDriveTedsGroundLeg:
    case RouteLegTypeEnum.TxCenterProviderGroundLeg:
    case RouteLegTypeEnum.CurriServiceGroundLeg:
      return 'solid';
    default:
      return 'solid';
  }
};

export const LegConnector = ({
  type,
  startConnection,
  endConnection,
  segmentType,
  isDisableAddLegButton = false,
  disableHintText = '',
  showStartConnector = true,
  showBottomConnector = false,
  showAddLegButton = false,
  onAddLegClick = () => {},
}: LegConnectorProps) => {
  const startConnectorColor = getConnectorColor({ connection: startConnection, segmentType });
  const endConnectorColor = getConnectorColor({ connection: endConnection, segmentType });
  const borderStyle = getBorderStyle({ type, segmentType });
  const lineColor = getMainConnectorColor(segmentType);
  const isRepositionLeg = [SegmentTypeEnum.reposition, SegmentTypeEnum.returnToBase, SegmentTypeEnum.custom].includes(
    segmentType as SegmentTypeEnum,
  );
  const borderWidthClass = isRepositionLeg ? 'border-2' : 'border-4';
  const isAirReposition =
    isRepositionLeg &&
    [
      RouteLegTypeEnum.TmdxJetAviaLeg,
      RouteLegTypeEnum.SupplementalLiftAviaLeg,
      RouteLegTypeEnum.TxCenterProviderAviaLeg,
    ].includes(type);
  const lineStyles = isAirReposition
    ? {
        backgroundImage: `linear-gradient(to bottom, ${lineColor} 66%, rgba(255, 255, 255, 0) 0%)`,
        backgroundPosition: 'left',
        backgroundSize: '4px 15px',
        backgroundRepeat: 'repeat-y',
      }
    : {
        borderColor: lineColor,
        borderStyle: `none ${borderStyle} none none`,
      };

  return (
    <div className="relative flex flex-col shrink-0 justify-center items-center w-5">
      {showStartConnector && (
        <div
          className="absolute -top-px box-border w-3.5 h-3.5 bg-white border-3 rounded-full"
          style={{ borderColor: startConnectorColor }}
        />
      )}
      <div
        className={`${isAirReposition ? 'w-0.5' : 'w-px'} ${isAirReposition ? '' : borderWidthClass} h-full`}
        style={lineStyles}
      />
      {showBottomConnector && !showAddLegButton && (
        <div
          className="absolute -bottom-px box-border w-3.5 h-3.5 bg-white border-3 rounded-full"
          style={{ borderColor: endConnectorColor }}
        />
      )}
      {showAddLegButton && (
        <Tooltip isDisabled={!isDisableAddLegButton} content={disableHintText} showArrow closeDelay={0}>
          <div className="absolute -bottom-2">
            <IconButton
              size="xs"
              variant="action"
              startContent={<PlusRoundFilled />}
              onPress={onAddLegClick}
              isDisabled={isDisableAddLegButton}
              className={isDisableAddLegButton ? 'text-base-30 opacity-100' : ''}
            />
          </div>
        </Tooltip>
      )}
    </div>
  );
};
